import { useDomainFromUrl } from '@/app/_common/domain/use-domain-from-url'
import { RateLimitIncreaseDialog } from '@/app/_common/rate-limit-increase-dialog'
import { Button } from '@/components/ui/button'
import { toast } from '@/components/ui/toaster'
import { useFetchAndParseMessages } from '@/hooks/api/use-messages-query'
import { Turn, TurnStatus, turnStatusKeyFactory } from '@/hooks/api/use-turn-status-query'
import { useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query'
import React from 'react'
import { isTurnStatusLoading } from './plan-steps/step-status'
import type { ChatTurnEntry } from './use-turn-parsed'

interface ChatData {
  title: string
  status: TurnStatus
  pinned?: boolean
  prompt?: string
  turns: ChatTurnEntry[]
  activeTurnId: string | null
}

function useChatDataQuery({
  workflowId,
  queryOptions,
}: {
  workflowId: string
  queryOptions?: Omit<UseQueryOptions<ChatData>, 'queryKey' | 'queryFn'>
}) {
  const { domainId } = useDomainFromUrl()
  const fetchChatData = useFetchChatData({ workflowId })

  return useQuery<ChatData>({
    queryKey: chatDataKeyFactory({ workflowId, domainId }),
    queryFn: fetchChatData,
    enabled: Boolean(workflowId),
    ...queryOptions,
  })
}

const PROMPT_NOT_FOUND_ERROR = 'PROMPT_NOT_FOUND'
const TOAST_ID_TOO_MANY_REQUESTS = 'TOO_MANY_REQUESTS'
const CREATE_WORKFLOW_429_ERROR = 'TOO_MANY_CREATE_WORKFLOW_REQUESTS'

function handlePromptSubmitError(
  error: string,
  defaultMessage: string = 'Failed to submit prompt',
) {
  if (error === PROMPT_NOT_FOUND_ERROR) {
    toast.error('Please enter a prompt')
  } else if (error === CREATE_WORKFLOW_429_ERROR) {
    toast.error(
      <span>
        {'Request limit reached. Please '}
        <RateLimitIncreaseDialog getToastId={() => TOAST_ID_TOO_MANY_REQUESTS}>
          <Button className='underline' unstyled>
            submit a request
          </Button>
        </RateLimitIncreaseDialog>
        {'  for more.'}
      </span>,
      { duration: Infinity, id: TOAST_ID_TOO_MANY_REQUESTS },
    )
  } else {
    toast.error(defaultMessage)
  }
}

const chatDataKeyFactory = ({ workflowId, domainId }: { workflowId: string; domainId: string }) => [
  'chat-data',
  workflowId,
  domainId,
]

// ------ Data Fetching ------

/**
 * Fetches messages and returns basic chat info without turn data
 */
const useFetchBasicChatData = ({ workflowId }: { workflowId: string }) => {
  const fetchMessages = useFetchAndParseMessages()

  return React.useCallback(async () => {
    const resp = await fetchMessages({ workflowId })

    // Extract unique turn IDs from messages
    const turnIds = Array.from(
      new Set(resp.messages?.flatMap((m) => (m.turnId ? [m.turnId] : [])) || []),
    )

    return {
      basicData: {
        title: resp.title ?? 'Chat',
        status: resp.status,
        pinned: resp.pinned,
        prompt: resp.prompt,
      },
      messages: resp.messages || [],
      turnIds,
    }
  }, [fetchMessages, workflowId])
}

/**
 * Main chat data fetching function - now uses individual turn queries
 */
const useFetchChatData = ({ workflowId }: { workflowId: string }) => {
  const fetchBasicData = useFetchBasicChatData({ workflowId })
  const queryClient = useQueryClient()
  const { domainId } = useDomainFromUrl()

  return React.useCallback(async () => {
    // Fetch basic chat data first
    const { basicData, messages, turnIds } = await fetchBasicData()

    // Build turn entries - turn data will be fetched by individual queries
    const entries: ChatTurnEntry[] = []

    for (const turnId of turnIds) {
      const turnMessages = messages.filter((m) => m.turnId === turnId)
      const userMessage = turnMessages.find((m) => typeof m?.messageContent.content === 'string')
      const userInputContent = userMessage?.messageContent.content as string | undefined

      // Create a shell entry - the actual turn data will be fetched by useTurnStatusQuery
      let shellTurn: Turn = {
        turnId,
        moderation: {
          appropriateness: 'appropriate',
          policies: [],
          categories: [],
          emergenceGuardrails: false,
          nativeModelSafetyTrigger: false,
        },
        status: 'IN_PROGRESS',
        input: { content: userInputContent ?? '' },
        startTimeEpoch: 0,
        endTimeEpoch: 0,
        output: '',
        error: null,
        steps: [],
        assistantMessageId: null,
      }
      // Try to get the latest status from the cache
      const cachedTurn = queryClient.getQueryData<Turn>(
        turnStatusKeyFactory({ workflowId, turnId, domainId }),
      )
      if (cachedTurn) {
        shellTurn = { ...shellTurn, ...cachedTurn }
      }
      entries.push({
        type: 'turn',
        turnId,
        turn: shellTurn,
        messages: turnMessages,
      })
    }

    const lastTurn = entries.findLast((t) => t.type === 'turn')
    const activeTurnId =
      lastTurn && isTurnStatusLoading(lastTurn.turn.status) ? lastTurn.turnId : null

    return {
      ...basicData,
      turns: entries,
      activeTurnId,
    }
  }, [fetchBasicData, queryClient, workflowId, domainId])
}

export {
  chatDataKeyFactory,
  CREATE_WORKFLOW_429_ERROR,
  handlePromptSubmitError,
  PROMPT_NOT_FOUND_ERROR,
  TOAST_ID_TOO_MANY_REQUESTS,
  useChatDataQuery,
  type ChatData,
}
